import { useState } from "react";
import { DialogForm, Field, FieldError, FieldLabel } from "@athena/ui";
import { useTranslation } from "react-i18next";
import { useMutation } from "@apollo/client/react";
import { CreatePresenterDocument, PresentersDocument } from "../../graphql/presenters.generated";
import { OrganizationSelect } from "@/components/OrganizationSelect";

interface NewPresentersDialogProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
}

/**
 * Presenters belong to an organization, and only that organization sees them — so the owner has to be
 * chosen here rather than defaulted.
 *
 * There is no "global" option on purpose. A presenter with no organization is visible to no partner at
 * all; the platform's own presenters belong to the MANUAL organization like everyone else's.
 */
export const NewPresentersDialog = ({ open, onOpenChange }: NewPresentersDialogProps) => {
    const { t } = useTranslation();
    const [
        organizationId,
        setOrganizationId,
    ] = useState<string>("");
    const [
        error,
        setError,
    ] = useState<string | null>(null);

    const [createPresenter] = useMutation(CreatePresenterDocument, {
        refetchQueries: [{ query: PresentersDocument }],
    });

    const handleSubmit = async ({ name, title, bio }: { name: string; title: string; bio: string }) => {
        if (!organizationId) {
            setError("Choose the organization this presenter belongs to.");
            return;
        }
        setError(null);
        await createPresenter({
            variables: { input: { organizationId, name, title: title || undefined, bio: bio || undefined } },
        });
        setOrganizationId("");
    };

    return (
        <DialogForm
            open={open}
            onOpenChange={onOpenChange}
            title={t("admin.lms.newPresenter")}
            description={t("admin.lms.newPresenterSubtitle")}
            labelName={t("admin.lms.fullName")}
            labelNamePlaceholder={t("admin.lms.fullNamePlaceholder")}
            labelTitleRole={t("admin.lms.titleRole")}
            labelTitleRolePlaceholder={t("admin.lms.titleRolePlaceholder")}
            labelTitleRoleHelper={t("admin.lms.titleRoleHelper")}
            labelBio={t("admin.lms.shortBio")}
            labelBioPlaceholder={t("admin.lms.shortBioPlaceholder")}
            labelCancel={t("common.actions.cancel")}
            labelSubmit={t("admin.lms.addPresenter")}
            onSubmit={handleSubmit}
        >
            <Field>
                <FieldLabel htmlFor="presenter-org" required>
                    Organization
                </FieldLabel>
                <OrganizationSelect
                    id="presenter-org"
                    aria-label="Organization"
                    aria-invalid={!!error}
                    placeholder="Choose an organization"
                    value={organizationId}
                    onValueChange={(value) => {
                        setOrganizationId(value);
                        setError(null);
                    }}
                />
                <FieldError>{error}</FieldError>
            </Field>
        </DialogForm>
    );
};
